import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'

const C = {
  g900:'#0D2218', g800:'#1A3A28', g700:'#235233', g600:'#2D6B42', g500:'#3D8A57',
  g100:'#E8F5EE', g50:'#F2FAF5', headerGrad:'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)',
}
const U = { bg:'#F6F8FB', card:'#FFFFFF', border:'#E8EDF3', text:'#1A2233', muted:'#6B7A90', primary:'#2563EB', primarySoft:'#EAF1FE' }

const STATUS_COLORS = {
  pending:   { bg:'#FEF9F0', color:'#B7770D', border:'#FCEDC7' },
  confirmed: { bg:'#EBF8F0', color:'#1A6B3C', border:'#A3DFB8' },
  completed: { bg:'#EDF2FF', color:'#3451B2', border:'#BAC8FF' },
  cancelled: { bg:'#FFF0F0', color:'#C92A2A', border:'#FFC9C9' },
}

const fmtDate = d => {
  if (!d) return '—'
  try {
    const s = typeof d === 'string' ? (d.includes('T') ? d.split('T')[0] : d.split(' ')[0]) : new Date(d).toISOString().slice(0,10)
    const [y,m,day] = s.split('-'); return `${day}/${m}/${y}`
  } catch { return String(d) }
}

// ── Status pill for follow-up calls ──────────────────────────────────────────
function CallStatusPill({ log }) {
  const map = {
    initiated: ['#B7770D','#FEF9F0','Calling…'],
    completed: ['#1A6B3C','#EBF8F0','Completed'],
    failed:    ['#C92A2A','#FFF0F0','Failed'],
  }
  const [color,bg,label] = map[log.status] || ['#6B7A90','#F1F3F7', log.status]
  return <span style={{ fontSize:'.68rem', fontWeight:700, color, background:bg, padding:'.15rem .5rem', borderRadius:999 }}>{label}</span>
}

// ── Follow-up Call History section ───────────────────────────────────────────
function FollowUpCallLogs({ appointmentId, fetchWithAuth }) {
  const [logs, setLogs] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [expanded, setExpanded] = React.useState(null)

  const fetchLogs = React.useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/voice/followup-logs?appointmentId=${appointmentId}`)
      if (!res.ok) throw new Error()
      const data = await res.json()
      setLogs(data.logs || [])
    } catch(e) { setLogs([]) } finally { setLoading(false) }
  }, [appointmentId, fetchWithAuth])

  React.useEffect(() => { fetchLogs() }, [fetchLogs])

  return (
    <div style={{ marginTop:'.9rem', borderTop:`1px solid ${U.border}`, paddingTop:'.9rem' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'.6rem' }}>
        <div style={{ fontSize:'.78rem', fontWeight:700, color:U.text, display:'flex', alignItems:'center', gap:6 }}>
          🤖 AI Follow-up Calls <span style={{ fontWeight:400, color:U.muted, fontSize:'.68rem' }}>(Bolna + Sarvam)</span>
        </div>
        <button onClick={fetchLogs} style={{ fontSize:'.7rem', color:U.primary, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}>Refresh</button>
      </div>

      {loading ? (
        <div style={{ fontSize:'.78rem', color:U.muted }}>Loading call history…</div>
      ) : logs.length === 0 ? (
        <div style={{ fontSize:'.78rem', color:U.muted }}>No follow-up calls yet for this appointment.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'.5rem' }}>
          {logs.map(log => (
            <div key={log.id} style={{ border:`1px solid ${U.border}`, borderRadius:10, padding:'.6rem .75rem', background:U.bg }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <CallStatusPill log={log} />
                  <span style={{ fontSize:'.75rem', color:U.muted }}>{log.call_date || log.createdAt?.slice(0,10)}</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:'.7rem' }}>
                  {log.symptoms_ok  === 1 && <span style={{ color:'#1A6B3C' }}>✓ Symptoms OK</span>}
                  {log.meds_ok      === 1 && <span style={{ color:'#1A6B3C' }}>✓ Meds taken</span>}
                  {log.wants_booking === 1 && <span style={{ color:U.primary }}>📅 Wants booking</span>}
                  {log.summary && (
                    <button onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                      style={{ color:U.primary, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
                      {expanded === log.id ? 'Hide' : 'View summary'}
                    </button>
                  )}
                </div>
              </div>
              {expanded === log.id && log.summary && (
                <div style={{ marginTop:'.5rem', fontSize:'.78rem', color:U.text, background:U.card, border:`1px solid ${U.border}`, borderRadius:8, padding:'.5rem .7rem', whiteSpace:'pre-wrap', lineHeight:1.6 }}>
                  {log.summary}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main PatientDetails component ────────────────────────────────────────────
const PatientDetails = ({ emailProp, onClose } = {}) => {
  const params = useParams()
  const email = emailProp || params.email
  const navigate = useNavigate()
  const isModal = !!onClose
  const { user, fetchWithAuth } = useAuth()
  const [appointments, setAppointments] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [savingId, setSavingId] = React.useState(null)

  React.useEffect(() => {
    const fetchDetails = async () => {
      if (!user?.token) return
      setLoading(true)
      try {
        const res = await fetchWithAuth(`${API_BASE}/api/patient/${encodeURIComponent(email)}`)
        if (!res.ok) throw new Error('Fetch failed')
        const data = await res.json()
        setAppointments(data.appointments || [])
      } catch(e) {
        console.error('Could not fetch patient details', e)
        setAppointments([])
      } finally { setLoading(false) }
    }
    fetchDetails()
  }, [email, user, fetchWithAuth])

  // Close on Escape when used as a modal
  React.useEffect(() => {
    if (!onClose) return
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const patientName = appointments.find(a => a.name)?.name || ''

  const triggerFollowupCall = async (appt) => {
    if (!window.confirm(`Call ${appt.name || 'patient'} now for AI follow-up?`)) return
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/voice/followup-call`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentId: appt.id, phone: appt.phone, name: appt.name, diagnosis: appt.diagnosis, medication: appt.medication })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Failed')
      alert('✅ AI follow-up call initiated! The patient will receive a call shortly.')
    } catch(e) { alert('Error: ' + e.message) }
  }

  const updateStatus = async (a, status) => {
    if (status === a.status) return
    setSavingId(a.id)
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/appointments/${a.id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ status }) })
      if (!res.ok) throw new Error('Update failed')
      setAppointments(prev => prev.map(item => item.id === a.id ? { ...item, status } : item))
    } catch(e) { console.error(e); alert('Could not update status') }
    finally { setSavingId(null) }
  }

  return (
    <div style={ isModal
      ? { width:'100%', maxWidth:820, maxHeight:'92vh', display:'flex', flexDirection:'column', background:U.bg, borderRadius:18, overflow:'hidden', boxShadow:'0 24px 64px rgba(13,34,24,.25)', fontFamily:"'DM Sans',sans-serif", color:U.text }
      : { minHeight:'100vh', background:U.bg, fontFamily:"'DM Sans',sans-serif", color:U.text } }>
      {/* Header */}
      <div style={{ background:C.headerGrad, padding:'2rem 1.5rem', position:'relative', flexShrink:0 }}>
        <div style={{ maxWidth:960, margin:'0 auto' }}>
          {isModal ? (
            <button onClick={onClose} aria-label="Close"
              style={{ position:'absolute', top:14, right:14, width:36, height:36, borderRadius:9, background:'rgba(255,255,255,.14)', border:'1px solid rgba(255,255,255,.25)', color:'#fff', cursor:'pointer', fontSize:'1.5rem', lineHeight:1, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
          ) : (
            <button onClick={() => navigate('/doctor-dashboard')}
              style={{ background:'rgba(255,255,255,.12)', border:'1px solid rgba(255,255,255,.2)', color:'#fff', padding:'.35rem .8rem', borderRadius:8, cursor:'pointer', fontSize:'.75rem', fontFamily:'inherit', marginBottom:'.9rem' }}>
              ← Back to dashboard
            </button>
          )}
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:52, height:52, borderRadius:'50%', background:'rgba(255,255,255,.15)', border:'2px solid rgba(255,255,255,.25)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontFamily:"'DM Serif Display',serif", fontSize:'1.4rem' }}>
              {(patientName || email || '?')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.5rem', color:'#fff' }}>{patientName || 'Patient'}</div>
              <div style={{ fontSize:'.82rem', color:'rgba(255,255,255,.6)' }}>{email}{appointments.length ? ` · ${appointments.length} visit${appointments.length===1?'':'s'}` : ''}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth:960, margin:'0 auto', padding:'1.75rem 1.5rem 3rem', width:'100%', ...(isModal ? { overflowY:'auto', flex:1, minHeight:0 } : {}) }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:'3rem', color:U.muted }}>Loading patient history…</div>
        ) : appointments.length === 0 ? (
          <div style={{ textAlign:'center', padding:'3rem', color:U.muted, background:U.card, border:`1px solid ${U.border}`, borderRadius:16 }}>
            <div style={{ fontSize:'2rem', marginBottom:'.75rem', opacity:.25 }}>📋</div>
            No appointments found for this patient.
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
            {appointments.map(a => {
              const sc = STATUS_COLORS[a.status] || STATUS_COLORS.pending
              return (
                <div key={a.id} style={{ background:U.card, border:`1px solid ${U.border}`, borderRadius:16, padding:'1.1rem 1.25rem', boxShadow:'0 1px 2px rgba(16,24,40,.04)' }}>
                  {/* Header row */}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'1rem', flexWrap:'wrap' }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                        <span style={{ fontSize:'.95rem', fontWeight:700, color:U.text }}>{a.service || 'Consultation'}</span>
                        <span style={{ fontSize:'.68rem', fontWeight:600, padding:'.2rem .6rem', borderRadius:999, background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, textTransform:'capitalize' }}>{a.status || 'pending'}</span>
                      </div>
                      <div style={{ fontSize:'.76rem', color:U.muted, marginTop:'.3rem' }}>
                        📅 {fmtDate(a.date)}{a.timeSlot ? ` · ${a.timeSlot}` : ''}
                        {a.followUpDate && <span style={{ marginLeft:8, color:U.primary, background:U.primarySoft, padding:'.1rem .5rem', borderRadius:999 }}>Follow-up: {fmtDate(a.followUpDate)}</span>}
                      </div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <button onClick={() => triggerFollowupCall(a)}
                        style={{ padding:'.4rem .8rem', fontSize:'.72rem', fontWeight:600, background:'#7C3AED', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontFamily:'inherit' }}>
                        🤖 Call Patient
                      </button>
                      <select defaultValue={a.status} id={`status-${a.id}`}
                        style={{ padding:'.4rem .6rem', border:`1px solid ${U.border}`, borderRadius:8, fontSize:'.75rem', fontFamily:'inherit', background:U.card, color:U.text }}>
                        {['pending','confirmed','completed','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button disabled={savingId===a.id} onClick={() => { const sel = document.getElementById(`status-${a.id}`); updateStatus(a, sel?.value) }}
                        style={{ padding:'.4rem .8rem', fontSize:'.72rem', fontWeight:600, background:U.card, color:U.text, border:`1px solid ${U.border}`, borderRadius:8, cursor:'pointer', fontFamily:'inherit' }}>
                        {savingId===a.id ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>

                  {a.message && <div style={{ marginTop:'.7rem', fontSize:'.82rem', color:U.text, lineHeight:1.6 }}>{a.message}</div>}

                  {a.diagnosis && (
                    <div style={{ marginTop:'.7rem', background:U.bg, borderRadius:10, padding:'.7rem .85rem' }}>
                      <div style={{ fontSize:'.64rem', fontWeight:700, color:U.muted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'.25rem' }}>🔬 Diagnosis</div>
                      <div style={{ fontSize:'.82rem', color:U.text, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{a.diagnosis}</div>
                    </div>
                  )}
                  {(a.prescription || a.medication) && (
                    <div style={{ marginTop:'.6rem', background:U.bg, borderRadius:10, padding:'.7rem .85rem' }}>
                      <div style={{ fontSize:'.64rem', fontWeight:700, color:U.muted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'.25rem' }}>💊 Prescription</div>
                      <div style={{ fontSize:'.82rem', color:U.text, lineHeight:1.6, whiteSpace:'pre-wrap', fontFamily:"'DM Sans',sans-serif" }}>{a.prescription || a.medication}</div>
                    </div>
                  )}

                  <FollowUpCallLogs appointmentId={a.id} fetchWithAuth={fetchWithAuth} />
                </div>
              )
            })}
          </div>
        )}
      </div>

      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;600;700&display=swap');`}</style>
    </div>
  )
}

export default PatientDetails
